import { useDevice } from '@tecma/ds';
import { useEffect, useState } from 'react';
import { useTranslation } from '@tecma/i18n';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import paths from 'routes/routesPaths';
import { advanceApplicationStatus, callExternalSendRequest, getExtraSpacesByIds } from 'store/sagas/configurations/configurations.api';
import { applicationStateActions } from 'store/slices/applicationState/slice';
import { applicationStateSelectors } from 'store/slices/applicationState/selectors';
import { selectCurrentUser } from 'store/slices/auth/selectors';
import { environmentSelectors } from 'store/slices/environment/selector';
import { buildExternalRequestBody } from 'utils/buildExternalRequestBody';

import type { ExternalRequestSpace } from 'utils/buildExternalRequestBody';
import type { Quote } from 'types/quote';

interface Props {
  quotes: Quote[];
  apartmentName: string;
  isSendRequestFlow?: boolean;
  onClose?: () => void;
  space?: ExternalRequestSpace;
}

export const useQuoteDetailsModal = ({ quotes, apartmentName, isSendRequestFlow = false, onClose, space }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPreviousQuotes, setShowPreviousQuotes] = useState(false);
  const [isQuoteDetailsModalOpen, setIsQuoteDetailsModalOpen] = useState(false);
  const [isSendRequestModalOpen, setIsSendRequestModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | undefined>(undefined);
  const projectId = useSelector(applicationStateSelectors.selectProjectId);
  const area = useSelector(applicationStateSelectors.selectArea);
  const assetTypology = useSelector(applicationStateSelectors.selectAssetTypology);
  const externalApi = useSelector(applicationStateSelectors.selectExternalApi);
  const user = useSelector(selectCurrentUser);
  const environment = useSelector(environmentSelectors.environment);
  const device = useDevice();
  const isDesktop = device.type === 'desktop';
  const quoteList = quotes
    .filter((sidebarQuote) => (isSendRequestFlow ? sidebarQuote.status === 'init' : true))
    .map((sidebarQuote) => ({
      ...sidebarQuote,
      onClick: () => setSelectedQuote(sidebarQuote),
    }));

  const getModalTitle = () => {
    if (isSendRequestFlow) {
      return t('send-request.title');
    }
    if (isDesktop || (quoteList && !selectedQuote)) {
      if (showPreviousQuotes) {
        return t('details-page.configuration-history');
      }
      return apartmentName;
    }
    return apartmentName;
  };

  const getModalDescription = () => {
    if (isSendRequestFlow) {
      return t('send-request.subtitle');
    }
    return '';
  };

  const onSelectQuote = (quoteId: string) => {
    if (!isQuoteDetailsModalOpen) setIsQuoteDetailsModalOpen(true);
    setSelectedQuote(quotes.find((quote) => quote.id === quoteId));
  };

  const openQuoteWithPreviousQuotes = () => {
    setShowPreviousQuotes(true);
    setIsQuoteDetailsModalOpen(true);
  };

  const toggleQuoteDetailsModal = () => {
    setIsQuoteDetailsModalOpen(!isQuoteDetailsModalOpen);
  };

  const handleOnClose = () => {
    if (!isDesktop && showPreviousQuotes && quoteList.length && selectedQuote) {
      setSelectedQuote(undefined);
    } else if (onClose) {
      onClose();
    } else {
      setIsQuoteDetailsModalOpen(false);
    }
  };

  const handleSendRequest = async () => {
    setIsSendRequestModalOpen(false);
    setIsQuoteDetailsModalOpen(false);
    if (selectedQuote?.id) {
      try {
        if (externalApi) {
          if (!user || !space || !projectId) {
            throw new Error('Missing data for external send request');
          }
          const extraIds = selectedQuote.features?.map((feature) => feature._id ?? '').filter(Boolean) ?? [];
          const extras = await getExtraSpacesByIds(projectId, extraIds);
          const body = buildExternalRequestBody({
            quote: selectedQuote,
            projectId,
            user,
            space,
            assetTypology,
            clientApiBaseUrl: environment.REACT_APP_CLIENT_API_REST_URI,
            namespace: environment.REACT_APP_NAMESPACE,
            extras,
          });
          await callExternalSendRequest(externalApi, body);
        } else {
          const response = await advanceApplicationStatus(selectedQuote.id, projectId ?? '', { area, assetTypology });
          if (response.status !== 'OK') {
            throw new Error('Failed to advance application status');
          }
        }
        navigate(paths.requestSent);
      } catch (error) {
        dispatch(
          applicationStateActions.setSnackbar({
            title: t('snackbar.error.send-request-failed.title'),
            description: t('snackbar.error.send-request-failed.description'),
            open: true,
            type: 'error',
          }),
        );
      }
    }
  };

  useEffect(() => {
    if (!isQuoteDetailsModalOpen && showPreviousQuotes) {
      setShowPreviousQuotes(false);
    }
  }, [isQuoteDetailsModalOpen]);

  useEffect(() => {
    if (isQuoteDetailsModalOpen) {
      if ((isDesktop || isSendRequestFlow) && !selectedQuote && quoteList?.length) {
        setSelectedQuote(quoteList[0]);
        if (quoteList?.length === 1) {
          setIsSendRequestModalOpen(true);
        }
      }
    } else if (selectedQuote) {
      setSelectedQuote(undefined);
    }
  }, [isQuoteDetailsModalOpen, device.type]);

  return {
    isQuoteDetailsModalOpen,
    selectedQuote,
    openQuoteWithPreviousQuotes,
    toggleQuoteDetailsModal,
    setSelectedQuote,
    handleSendRequest,
    quoteList,
    handleOnClose,
    isDesktop,
    onSelectQuote,
    showPreviousQuotes,
    setShowPreviousQuotes,
    getModalTitle,
    getModalDescription,
    isSendRequestModalOpen,
    setIsSendRequestModalOpen,
  };
};
